import {CardiologsSentenceSelector} from "./cardiologs-sentence-selector";
import {ConfigField} from "./sentence-selector";
import {ExternalFindings} from "../../../external-findings";

export class CasSentenceSelector extends CardiologsSentenceSelector {

  override selectAverageHeartRateSentence(): ConfigField[] {
    return ["findings_averageHeartRate"];
  }

  override selectSentenceForVentricularRuns(): ConfigField[] {
    const vRuns = this.externalFindings.vRuns;
    if (!vRuns) {
      return [];
    }
    return [vRuns === 1 ? "findings_oneVRun" : "findings_moreThanOneVRun"];
  }

  override selectSentenceForSupraventricularRuns(): ConfigField[] {
    const sRuns = this.externalFindings.sRuns;
    if (!sRuns) {
      return [];
    }
    return [sRuns === 1 ? "findings_oneSRun" : "findings_moreThanOneSRun"];
  }

  override selectSentenceForVentricularTachycardias(): ConfigField[] {
    const usedFields: (keyof ExternalFindings)[] = [
      "ventricularBeats",
      "ventricularBeatsBurden",
      "ventricularTachycardias",
    ];
    if (usedFields.some(field => this.externalFindings[field] === undefined)) {
      return [];
    }
    if (this.externalFindings.ventricularTachycardias !== 0
      && this.externalFindings.ventricularTachycardiasMaxHeartRate === undefined) {
      return [];
    }

    if (this.externalFindings.ventricularTachycardias! > 2
      && this.externalFindings.ventricularTachycardiasMaxHeartRate! > 10) {
      return [];
    }

    return [
      this.selectVentricularTachycardiasMainSentence(),
      this.selectVentricularTachycardiasAdditionalSentence()
    ].filter(sentence => sentence !== undefined);
  }

  private selectVentricularTachycardiasMainSentence(): ConfigField | undefined {
    if (this.externalFindings.ventricularTachycardias === 0) {
      if (this.externalFindings.ventricularBeatsBurden! > 1 && this.externalFindings.vRuns === 0) {
        return "findings_noVentricularTachycardias";
      }
      return undefined;
    }

    if (this.externalFindings.ventricularTachycardias === 1) {
      return "findings_oneVentricularTachycardia";
    }

    return this.selectCasVtSimplifiedFirstSentence();
  }

  private selectVentricularTachycardiasAdditionalSentence(): ConfigField | undefined {
    if (this.externalFindings.ventricularTachycardias! <= 1) {
      return undefined;
    }

    return "findings_moreThanOneVentricularTachycardias_additionalSentence";
  }

  override selectSentenceForSlowVentricularRhythms(): ConfigField[] {
    if (this.externalFindings.slowVentricularRhythmCount === undefined) {
      return [];
    }

    if (this.externalFindings.slowVentricularRhythmCount === 0) {
      return [];
    }

    if (this.externalFindings.slowVentricularRhythmCount === 1) {
      return ["findings_oneSlowVentricularRhythm"];
    }

    return ["findings_moreThanOneSlowVentricularRhythm"];
  }

  protected override areAnyAtrialTachychardiaFieldsUndefined(): boolean {
    const fields: (keyof ExternalFindings)[] = [
      "afibBurden",
      "atrialTachycardias",
      "atrialTachycardiasMaximumHeartRateDuration",
    ];

    if (fields.some(field => this.externalFindings[field] === undefined)) {
      return true;
    }

    if (this.externalFindings.atrialTachycardias !== 0
      && this.externalFindings.atrialTachycardiasMaximumHeartRate === undefined) {
      return true;
    }

    return false;
  }

  override selectSentenceForAtrialTachycardias(): ConfigField[] {
    if (this.areAnyAtrialTachychardiaFieldsUndefined()) {
      return [];
    }

    return [
      this.selectAtrialTachycardiasMainSentence(),
      this.selectAtrialTachycardiasAdditionalSentence()
    ].filter((sentence): sentence is ConfigField => sentence !== undefined);
  }

  private selectAtrialTachycardiasMainSentence(): ConfigField | undefined {
    if (this.prevailingRhythmValue === "AF") {
      return undefined;
    }

    if (this.externalFindings.atrialTachycardias === 0) {
      return "findings_noAtrialTachycardias";
    }

    if (this.externalFindings.atrialTachycardias === 1) {
      return "findings_oneAtrialTachycardia";
    }

    return this.selectCasAtSimplifiedFirstSentence();
  }

  private selectAtrialTachycardiasAdditionalSentence(): ConfigField | undefined {
    if (this.prevailingRhythmValue === "AF") {
      return undefined;
    }

    if (this.externalFindings.atrialTachycardias! <= 1) {
      return undefined;
    }

    return "findings_moreThanOneAtrialTachycardias_additionalSentence";
  }

  private selectCasVtSimplifiedFirstSentence(): ConfigField {
    if (this.externalFindings.ventricularTachycardias === 2) {
      return "findings_twoVentricularTachycardias";
    }
    return "findings_moreThanTwoVentricularTachycardias";
  }

  private selectCasAtSimplifiedFirstSentence(): ConfigField {
    if (this.externalFindings.atrialTachycardias === 2) {
      return "findings_twoAtrialTachycardias";
    }
    return "findings_moreThanTwoAtrialTachycardias";
  }
}
